import { createApp } from 'vue'
import { createPinia } from 'pinia'
import { VueQueryPlugin, QueryClient } from '@tanstack/vue-query'

import App from './App.vue'
import { router } from './router'
import { useSession } from '@/features/auth/composables/useSession'
import { useSessionStore } from '@/app/stores/session.store'
import { initApiClient } from '@/app/providers/api'
import { ApiError } from '@/shared/api'
// Side-effect import: the splash module calls mountSplash() at module
// init, so the QR is alive the moment this file's bundle parses. We
// only consume the unmount handle.
import { unmountSplash, showSplashError } from './splash'
import { startNetworkWatch } from '@/shared/lib/network'
import { redactErrorForLog } from '@/shared/lib/redact'
import { clearAllEzqrStorage, migrateLegacyKeys } from '@/shared/lib/scoped-storage'
import { env, envError } from '@/app/config/env'

import '@/assets/main.css'

// Reset escape hatch — recover from a poisoned localStorage (e.g. a
// stale `api-host`/`csrf` left by a different build deployed to the same
// origin). `?reset` wipes every `ezqr:*` record, then strips the query
// param and reloads so the app boots clean from env defaults. Allowed in
// ALL envs: clearing your own session state is not a security concern,
// and the failure mode it fixes can happen anywhere. MUST run before any
// Pinia store is created, since stores read localStorage during setup.
if (typeof window !== 'undefined') {
  try {
    const params = new URLSearchParams(window.location.search)
    if (params.has('reset')) {
      const n = clearAllEzqrStorage()
      // eslint-disable-next-line no-console
      console.info(`[ezqr] reset: cleared ${n} localStorage record(s)`)
      params.delete('reset')
      const qs = params.toString()
      window.location.replace(
        window.location.pathname + (qs ? `?${qs}` : '') + window.location.hash,
      )
    }
  } catch {
    /* storage / URL API unavailable — nothing to reset */
  }
}

// Dev-only: capture `?forceShare` at boot — before the router can run
// its auth redirects (which would drop the query) — and keep it for the
// tab so the QR Share button can be revealed on platforms without Web
// Share (e.g. desktop Linux) for layout testing. Never honoured in prod.
if (!env.isProd && typeof window !== 'undefined') {
  try {
    const params = new URLSearchParams(window.location.search)
    if (params.has('forceShare')) {
      sessionStorage.setItem('ezqr:forceShare', '1')
    }
    // Dev-only: `?rawUpload` skips the client-side resize/WebP step so the
    // original file is sent untouched — useful for comparing against the
    // optimized path or diagnosing a server-side rejection. Persisted for
    // the tab, like forceShare. Never honoured in prod.
    if (params.has('rawUpload')) {
      sessionStorage.setItem('ezqr:rawUpload', '1')
    }
    // Dev-only: `?bulkStrategy=fanout|endpoint` pins which bulk
    // implementation the dish-list uses, overriding the default
    // selection (see src/features/dishes/api/bulk-strategy.ts).
    // Useful for verifying both branches in QA without redeploying.
    const bs = params.get('bulkStrategy')
    if (bs === 'fanout' || bs === 'endpoint') {
      sessionStorage.setItem('ezqr:bulkStrategy', bs)
    }
  } catch {
    /* sessionStorage unavailable — ignore */
  }
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      // Smarter retry policy: a single blip on transit / a transient
      // 5xx shouldn't surface as a user-visible failure card. But auth
      // errors (401/403), bad-request (400), not-found (404) — those
      // mean the request is wrong, retrying won't help. Same for
      // parse errors (we got a 2xx with a wrong shape) — the server
      // is going to keep returning the same shape.
      retry: (failureCount, error) => {
        if (failureCount >= 2) return false
        if (error instanceof ApiError) {
          if (error.kind === 'network' || error.kind === 'timeout') return true
          if (error.kind === 'unavailable') return true // 503
          if (error.status >= 500 && error.status < 600) return true
          return false
        }
        // Non-ApiError (unexpected) — be conservative, retry once.
        return failureCount < 1
      },
      // Linear back-off capped at 2s — keeps the second attempt
      // perceptibly distinct from the first without making the UI
      // hang.
      retryDelay: (attemptIndex) => Math.min(500 * 2 ** attemptIndex, 2000),
    },
  },
})

async function start() {
  // Fatal config error (env failed to parse) — surface it instead of
  // spinning the splash forever. env uses safeParse so the import
  // itself didn't throw; we bail here where we can show UI.
  if (envError) {
    console.error('[app] fatal config error', envError)
    showSplashError(envError)
    return
  }

  // Drop pre-scoping localStorage keys before any store reads storage.
  // One-shot, idempotent — see migrateLegacyKeys.
  migrateLegacyKeys()

  // Begin tracking connectivity before anything else — the offline
  // banner should be accurate from the first frame.
  startNetworkWatch()

  const app = createApp(App)

  // Backstop for errors the render-tree boundary (AppErrorBoundary)
  // can't catch — async event handlers, timers, etc. At minimum they
  // get logged with Vue's component/lifecycle context instead of
  // being swallowed; a real reporter (Sentry) would hook in here.
  //
  // Redact sensitive headers before logging. Axios attaches the
  // original request `config` (including the resolved Authorization
  // Bearer + cookies) to its error instances; the api client tries to
  // strip this via `translateAxiosError`, but anything that bypasses
  // that path (a stray `axios.get(...)` somewhere, a future plugin)
  // would otherwise leak the token straight into DevTools / Sentry /
  // PostHog. Cheap defence-in-depth.
  app.config.errorHandler = (err, _instance, info) => {
    console.error('[app] unhandled error', info, redactErrorForLog(err))
  }

  app.use(createPinia())
  app.use(VueQueryPlugin, { queryClient })

  // Wire the api client AFTER Pinia is installed (it reads the session
  // store at build time) and BEFORE bootstrap (which uses the client).
  // `onAuthLost` lives here — `providers/api.ts` itself stays
  // router-free so any module touching the api doesn't transitively
  // pull the route graph.
  initApiClient({
    onAuthLost() {
      useSessionStore().clear()
      // Don't await — router push may bounce through guards.
      void router.push({ name: 'login' })
    },
  })

  // Bootstrap the session BEFORE installing the router. Router guards
  // read isAuthenticated to decide redirects, so they must see the
  // restored session on the very first navigation. Without this the
  // user briefly bounces to /auth/login on every reload.
  //
  // Pinia must be active here, hence the order above; router is added
  // after the bootstrap settles.
  await useSession().bootstrap()

  app.use(router)
  await router.isReady()
  app.mount('#app')
  // Fade the splash out once the first view is on screen. Failed
  // bootstrap (network, refresh refusal) still falls through to a
  // mount on /auth/login, so the splash always lifts eventually.
  unmountSplash()
}

// Any unexpected throw during bootstrap/mount must not leave the splash
// spinning forever — surface a fatal-error screen with a reload action.
// (Expected failures like a refused refresh are handled inside
// `bootstrap()` and fall through to a normal /auth/login mount.)
void start().catch((err: unknown) => {
  console.error('[app] fatal bootstrap error', redactErrorForLog(err))
  showSplashError(err instanceof Error ? err.message : undefined)
})
